import React from 'react';
import { motion, Variants } from 'framer-motion';

interface ScaleInProps {
  children: React.ReactNode;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Initial scale value (0-1) */
  initialScale?: number;
  /** Use spring physics for bounce effect */
  spring?: boolean;
  /** Animation duration (in seconds, ignored if spring is true) */
  duration?: number;
  /** Custom className for styling */
  className?: string;
  /** Transform origin point */
  transformOrigin?: string;
}

/**
 * ScaleIn - Scale entrance animation
 *
 * Scales content from a smaller size (with opacity 0) to full size.
 * Uses spring physics by default for a satisfying bounce effect.
 * Perfect for modals, cards, badges, and other UI elements that should "pop in".
 * Respects prefers-reduced-motion accessibility preference.
 *
 * @example
 * ```tsx
 * // Default spring animation
 * <ScaleIn>
 *   <Dialog />
 * </ScaleIn>
 *
 * // Custom scale and delay
 * <ScaleIn initialScale={0.8} delay={0.2}>
 *   <Badge />
 * </ScaleIn>
 *
 * // Transform from top-left corner
 * <ScaleIn transformOrigin="top left">
 *   <Tooltip />
 * </ScaleIn>
 * ```
 */
export const ScaleIn: React.FC<ScaleInProps> = ({
  children,
  delay = 0,
  initialScale = 0.9,
  spring = true,
  duration = 0.3,
  className,
  transformOrigin = 'center',
}) => {
  const variants: Variants = {
    hidden: {
      opacity: 0,
      scale: initialScale,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: spring
        ? {
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay,
          }
        : {
            duration,
            delay,
            ease: [0.4, 0.0, 0.2, 1], // Material Design easing
          },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={variants}
      className={className}
      style={{
        transformOrigin,
        willChange: 'transform, opacity',
      }}
    >
      {children}
    </motion.div>
  );
};
